import { Alert } from '@mui/material';

/**
 * Props for {@link AnnotationVisibilityNotice}.
 *
 * @property visible - Whether the notice is rendered. Parent toggles this on
 *   the first-annotation-create signal and back off when the user dismisses.
 * @property onDismiss - Fired when the user clicks the close button.
 */
type Props = {
  visible: boolean;
  onDismiss: () => void;
};

/**
 * Notice shown on a user's first annotation in the current viewer mount,
 * reminding them that saved annotations are visible to firm employees only
 * (clients don't see them). Dismissal is in-memory only — the notice
 * reappears the first time they annotate a different document.
 *
 * Uses MUI's Alert with severity="info" to match the look of other
 * informational banners in the firm app (e.g. OrganizerTemplates).
 *
 * DocumentPDFViewer mounts on firm and client legacy pages, but this copy never
 * reaches a client. SharedDocumentPDFViewer only enables annotations for
 * employees. PspdfkitDocumentPDFViewer calls the firm-scoped persistence API for
 * everyone, and for a client the load fails auth, so no annotation is created. If
 * persistence is opened to clients, revisit the copy first.
 */
export default function AnnotationVisibilityNotice({
  visible,
  onDismiss,
}: Props) {
  if (!visible) return null;

  return (
    <Alert
      severity="info"
      onClose={onDismiss}
      role="status"
      sx={{ borderRadius: 0, alignItems: 'center' }}
    >
      Annotations are saved for your firm only — clients don&rsquo;t see them.
    </Alert>
  );
}
